import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, IconButton, ListItemIcon, ListItemText, MenuItem, } from '@mui/material';
import MuiDialogContent from '@mui/material/DialogContent';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-pro';
import * as MaterialUIIcons from '../../controls/MaterialUIIcons';

import MuiDialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';
import { fetchLogs, refresh, selectAllLogs, selectLogById, } from '../../../../features/logSlice';
import { RootState, useAppDispatch } from '../../../store';
import { useTranslation } from 'react-i18next';
import Controls from '../../controls/Controls';
import makeStyles from '@mui/styles/makeStyles';
import { LogDialogDetails } from './LogDialogDetails';
import { Log } from '../../../../models';
import EditPanelContextMenu from '../EditPanelDataGrid/EditPanelCtxMenu';
import { getDateTimeFormattedColumn } from '../../../helpers/columnHelper';
import { ResizableBox } from 'react-resizable';
import { PaperComponent } from '../EditDialog/EditDialog';
import { useResizeStyles } from '../EditDialog/stylesDialog';

interface LogDialogProps {
  open: boolean;
  close(): void;
  subtitle: string;
  detailsMode: boolean;
  id?: string | number;
  logId?: number;
  onLogOpen: (id: string | number, logId: number) => void;
  goBackToLogList: () => void;
  logUrl: string;
}

export const useStyles = makeStyles((theme) => ({
  gridContainer: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  toolbar: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    padding: '0 0 0 1.5rem',
    height: 48,
  },
  btn: {
    paddingTop: 4,
    paddingBottom: 4,
    marginLeft: 10,
  },
  root: {
    '& ::-webkit-scrollbar': {
      height: theme.spacing(1),
    },
    '& ::-webkit-scrollbar-track': {
      background: theme.palette.grey[200],
    },
    '& ::-webkit-scrollbar-thumb': {
      background: theme.palette.grey[600],
    },
    '& ::-webkit-scrollbar-thumb:hover': {
      background: theme.palette.grey[600],
    },
    '& .MuiDataGrid-row.Mui-even:not(:hover)': {
      backgroundColor: 'rgba(10, 8, 8, 0.04)',
    },
    '& .MuiInputBase-input': {
      padding: theme.spacing(0.5, 1),
    },
    '& .MuiDataGrid-columnHeader': {
      padding: theme.spacing(0.5, 1.5),
      fontSize: 14,
      '& .MuiDataGrid-columnHeaderTitleContainer': {
        padding: 0,
        '& .MuiDataGrid-columnHeaderTitle': {
          fontWeight: 'bold',
        },
      },
    },
    '& .checkbox--cell': {
      '& .MuiFormControlLabel-root': {
        margin: 0,
      },
    },
    '&.MuiDataGrid-root': {
      padding: theme.spacing(0),
    },
    '& .MuiCircularProgress-root': {
      zIndex: theme.zIndex.speedDial,
    },
  },
}));

export function LogDialog(props: LogDialogProps) {
  const { t } = useTranslation();
  const [ctxMenuOpen, setCtxMenuOpen] = useState(false);
  const [currentElt, setCurrentElt] = useState<Log>();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();
  const [coordinatesOfMenu, setCoordinatesOfMenu] = useState<{
    X?: number;
    Y?: number;
  }>({});

  const dispatch = useAppDispatch();
  const logs = useSelector(selectAllLogs);
  const entityLog = useSelector((state: RootState) =>
    selectLogById(state, encodeURIComponent(props?.logId!))
  );
  const classes = useStyles();
  const apiRef = useGridApiRef();
  const logStatus = useSelector((state: RootState) => state.log.status);
  const logError = useSelector((state: RootState) => state.log.error);
  const [pageSize, setPageSize] = React.useState(10);
  const [page, setPage] = React.useState(0);
  const nextPage = useSelector((state: RootState) => state.log.nextPage);
  const count = useSelector((state: RootState) => state.log.count);

  const title = `Logg for element ${props.subtitle}`;

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: t('Id'),
      width: 75,
      sortable: true,
      valueFormatter: ({ value }) => Number(value),
    },
    {
      field: 'applikasjon',
      headerName: t('Applikasjon'),
      width: 200,
      sortable: true,
    },
    { field: 'type', headerName: t('Type'), width: 200, sortable: true },
    { field: 'gidId', headerName: t('GidID'), width: 100, sortable: true },
    { field: 'navn', headerName: t('Bruker'), width: 200, sortable: true },
    {
      field: 'tidspunkt',
      headerName: t('Tidspunkt'),
      width: 200,
      sortable: true,
      valueGetter: getDateTimeFormattedColumn,
    },
    {
      field: 'funksjon',
      headerName: t('Funksjon'),
      width: 150,
      sortable: true,
    },
    { field: 'modus', headerName: t('Modus'), width: 100, sortable: true },
    {
      field: 'tabellNavn',
      headerName: t('Tabell navn'),
      width: 200,
      sortable: true,
    },
    {
      field: 'elementId',
      headerName: t('Element id'),
      width: 100,
      sortable: true,
    },
    {
      field: 'gruppeLoggID',
      headerName: t('GruppeLogg id'),
      width: 100,
      sortable: true,
    },
    { field: 'merknad', headerName: t('Merknad'), width: 150, sortable: true },
  ];

  useEffect(() => {
    if (!props?.id)
      return;

    dispatch(refresh());
  }, [dispatch, props?.id]);

  useEffect(() => {
    if (!props?.open)
      return;
    if (!props?.logUrl || !props?.id)
      return;

    if (
      logStatus === 'idle' ||
      (nextPage && logs?.length < (page + 1) * pageSize)
    ) {
      dispatch(fetchLogs({ nextPage, logUrl: (props?.logUrl), id: encodeURIComponent(props?.id) }));
    }
  }, [
    dispatch,
    logStatus,
    logs,
    nextPage,
    page,
    pageSize,
    props.id,
    props.open,
    props.logUrl,
  ]);

  function handleContextMenu(e: any) {
    e.preventDefault();
    setCtxMenuOpen(false);
    const id = e.currentTarget.getAttribute('data-id');
    if (e?.currentTarget?.dataset?.rowindex) {
      setCoordinatesOfMenu({ X: e.clientX, Y: e.clientY });
      setCurrentElt(apiRef.current.getRow(encodeURIComponent(id)) as unknown as Log);
      setCtxMenuOpen(true);
      setAnchorEl(e.target);
    }
  }

  function handleOpenDetails() {
    if (currentElt?.id) {
      props.onLogOpen(props.id!, Number(currentElt?.id));
    }
    handleCloseCtxMenu();
  }

  function handleCloseCtxMenu() {
    setCtxMenuOpen(false);
    setAnchorEl(undefined);
    setCurrentElt(undefined);
  }

  function handlePageChange(p: number, details: any) {
    setPageSize(details.pageSize);
    setPage(p);
  }

  const { resizable } = useResizeStyles();
  return (
    <Dialog
      open={props.open}
      onClose={props.close}
      PaperComponent={PaperComponent}
      aria-labelledby='draggable-dialog-title'

    >
      <ResizableBox
        height={784}
        width={1540}
        className={resizable}
      >
        <>
          <MuiDialogTitle
            className={classes.toolbar}
            id='draggable-dialog-title'
            sx={{ justifyContent: 'space-between', display: 'flex' }}
          >
            <Typography variant='h2'
              fontWeight='bold'
              style={{ float: 'left', fontSize: '16px', alignSelf: 'center' }}
            >
              {' '}
              {props.detailsMode ? `Loggid: ${entityLog?.id}` : title}{' '}
            </Typography>
            <DialogActions>
              <Button
                color='secondary'
                variant='outlined'
                onClick={props.goBackToLogList}
                className={classes.btn}
                hidden={!props.detailsMode}
              >
                <Typography
                  variant='subtitle1'
                  component='h2'
                >
                  Gå tilbake til listen
                </Typography>
              </Button>
              <IconButton
                color='secondary'
                aria-label={t('Lukk')}
                onClick={props.close}
              >
                <MaterialUIIcons.CloseIcon color='secondary' />
              </IconButton>

            </DialogActions>
          </MuiDialogTitle>

          <MuiDialogContent sx={{ height: '46rem' }}>
            {props.detailsMode ? (
              <LogDialogDetails entity={entityLog ?? ({} as Log)} />
            ) : (
              <div
                style={{ width: '100%', height: '95%', marginTop: 18 }}
                className={classes.gridContainer}
              >
                <Controls.DataGrid
                  apiRef={apiRef}
                  rows={logs}
                  columns={columns}
                  className={classes.root}
                  headerHeight={40}
                  rowHeight={35}
                  rowCount={count}
                  page={page}
                  disableColumnMenu
                  autoPageSize
                  disableColumnSelector={true}
                  disableSelectionOnClick={true}
                  hideFooterPagination
                  hideFooter
                  error={logStatus === 'failed' ? logError : undefined}
                  onPageChange={handlePageChange}
                  loading={logStatus === 'loading'}
                  onRowClick={(r) => props.onLogOpen(props?.id!, Number(r?.id))}
                  componentsProps={{
                    row: {
                      onContextMenu: handleContextMenu,
                    },
                  }}
                />
                <EditPanelContextMenu
                  anchorEl={anchorEl}
                  open={ctxMenuOpen}
                  X={coordinatesOfMenu.X}
                  Y={coordinatesOfMenu.Y}
                  onClose={() => setCtxMenuOpen(false)}
                >
                  <MenuItem key={'details'} onClick={(r) => handleOpenDetails()}>
                    <ListItemIcon>
                      <IconButton color='primary'>
                        {' '}
                        <MaterialUIIcons.EditIcon fontSize='small' />
                      </IconButton>
                    </ListItemIcon>
                    <ListItemText primary={'Åpne logg'} />
                  </MenuItem>
                </EditPanelContextMenu>
              </div>
            )}
          </MuiDialogContent>
        </>
      </ResizableBox>
    </Dialog>
  );
}

export default LogDialog;
